<!--
  ImportWizard — หน้านำเข้าไฟล์ 5 ขั้นของ GraphRAG (S12-P1 · Task 4-A)

  1 อัปโหลด → 2 ตรวจเนื้อหา → 3 วันที่ → 4 จับคู่ระดับ → 5 ตรวจและสร้าง
  ไม่มีไฟล์ markdown ที่แก้ได้ = ข้ามขั้น 2 ทั้งขาไปและขากลับ (ลอกจาก nexa)

  🔴 มติ D10 — component นี้ถูก render อยู่ "ข้างใน" v-dialog ของ views/GraphEditor.vue
     ⇒ Vuetify 3 unmount เนื้อใน dialog หลังปิด ⇒ ห้ามเก็บ state ถาวร/timer ไว้ที่นี่
     state ทั้งหมดอยู่ที่ GraphEditor แล้วส่งลงมาเป็น prop `state` · เปลี่ยนค่าได้ทางเดียวคือ emit('patch')

  ห้ามสร้าง v-dialog ครอบตัวเอง (ตัวนอกมีอยู่แล้ว) · ใช้สีแบรนด์ #C32E0E เท่านั้น · ห้าม literal บนหน้าจอ
  testid ทุกตัวขึ้นต้น wiz- (wizard- ถูก StructuredImportWizard.vue จองไว้แล้ว)
-->
<template>
  <div class="wiz-root" data-testid="wiz-root">
    <v-card-text class="wiz-body">
      <v-alert v-if="!canEdit" type="warning" variant="tonal" density="compact" class="mb-3" data-testid="wiz-readonly-alert">
        {{ $t('graphRagReadOnly') }}
      </v-alert>

      <!-- นำเข้าใต้ Node ID — บอกปลายทางให้ชัดตั้งแต่ขั้นแรก -->
      <v-alert v-if="state.parentNodeKey" type="info" color="#C32E0E" variant="tonal" density="compact" class="mb-3" data-testid="ingest-parent-banner">
        {{ $t('graphRagImportUnderNodeBanner', { node: state.parentNodeKey }) }}
      </v-alert>

      <v-stepper :model-value="state.step" flat hide-actions class="wiz-stepper" @update:model-value="onStepperNav">
        <!-- แถบ 5 ขั้นแบบเต็มใช้ได้เฉพาะจอกว้าง — จอแคบเลื่อนแนวนอนแล้วแตะพลาด -->
        <v-stepper-header v-if="!mobile">
          <template v-for="(s, i) in steps" :key="s.value">
            <v-divider v-if="i > 0" />
            <v-stepper-item
              :value="s.value"
              :title="s.title"
              :subtitle="isSkipped(s.value) ? $t('graphRagWizStep2Skipped') : undefined"
              :complete="state.step > s.value && !isSkipped(s.value)"
              :editable="canGoto(s.value)"
              :aria-disabled="canGoto(s.value) ? 'false' : 'true'"
              :data-testid="'wiz-step-ind-' + s.value"
              color="#C32E0E"
              complete-icon="mdi-check"
            />
          </template>
        </v-stepper-header>

        <!-- จอแคบ: จุด + แถบความคืบหน้า แทน header แนวนอน -->
        <div v-else class="wiz-dots" data-testid="wiz-step-dots">
          <div class="wiz-dots-row">
            <span v-for="s in steps" :key="s.value" class="wiz-dot" :class="{ 'wiz-dot--on': state.step >= s.value }" />
          </div>
          <v-progress-linear :model-value="(state.step / steps.length) * 100" color="#C32E0E" height="4" rounded :aria-label="stepHeading" />
        </div>

        <div ref="stepHeading" class="wiz-step-heading" tabindex="-1" data-testid="wiz-step-heading">{{ stepHeading }}</div>

        <v-stepper-window class="wiz-window">
          <!-- ── ขั้น 1 อัปโหลด ───────────────────────────────────────── -->
          <v-stepper-window-item :value="1">
            <!-- ปุ่มสลับโหมด — ตัวรับ hover ต้องเป็น span ที่ครอบ เพราะปุ่มที่ disabled ไม่ยิง pointer event -->
            <v-tooltip :text="$t('graphRagWizModeLocked')" :disabled="!modeLocked" location="top">
              <template #activator="{ props }">
                <span v-bind="props" class="wiz-tip-wrap mb-3">
                  <v-btn-toggle :model-value="state.mode" mandatory density="comfortable" color="#C32E0E" :disabled="modeLocked || !canEdit">
                    <v-btn value="files" data-testid="wiz-mode-files" @click="switchMode('files')">{{ $t('graphRagWizModeFiles') }}</v-btn>
                    <!-- เบราว์เซอร์ที่เลือกทั้งโฟลเดอร์ไม่ได้ ต้อง "ไม่ render ปุ่ม" ไม่ใช่ disable เงียบ (§Folder Mode) -->
                    <v-btn v-if="folderSupported" value="folder" data-testid="wiz-mode-folder" @click="switchMode('folder')">{{ $t('graphRagWizModeFolder') }}</v-btn>
                  </v-btn-toggle>
                </span>
              </template>
            </v-tooltip>

            <v-alert v-if="!folderSupported" type="info" color="#C32E0E" variant="tonal" density="compact" class="mb-3" data-testid="wiz-folder-unsupported">
              {{ $t('graphRagWizFolderUnsupported') }}
            </v-alert>

            <div
              v-if="!folderMode"
              class="wiz-drop"
              :class="{ 'wiz-drop--over': dragOver, 'wiz-drop--off': !canEdit }"
              role="button"
              tabindex="0"
              data-testid="wiz-dropzone"
              :aria-label="$t('graphRagWizDropFiles')"
              @click="openPicker"
              @keydown.enter.prevent="openPicker"
              @keydown.space.prevent="openPicker"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onDrop"
            >
              <v-icon size="28" color="#C32E0E">mdi-tray-arrow-up</v-icon>
              <div class="wiz-drop-title">{{ mobile ? $t('graphRagWizDropFilesMobile') : $t('graphRagWizDropFiles') }}</div>
              <div v-if="!mobile" class="wiz-sub">{{ $t('graphRagWizDropFilesHint') }}</div>
              <div class="wiz-sub">{{ $t('graphRagWizAcceptedTypes') }}</div>
            </div>

            <!-- โหมดโฟลเดอร์: ลากทั้งโฟลเดอร์ได้ด้วย webkitGetAsEntry · คลิกแล้วเปิด picker ที่มี webkitdirectory -->
            <div
              v-else
              class="wiz-drop"
              :class="{ 'wiz-drop--over': dragOver, 'wiz-drop--off': !canEdit }"
              role="button"
              tabindex="0"
              data-testid="wiz-folder-dropzone"
              :aria-label="$t('graphRagWizDropFolder')"
              @click="openFolderPicker"
              @keydown.enter.prevent="openFolderPicker"
              @keydown.space.prevent="openFolderPicker"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onFolderDrop"
            >
              <v-icon size="28" color="#C32E0E">mdi-folder-upload-outline</v-icon>
              <div class="wiz-drop-title">{{ $t('graphRagWizDropFolder') }}</div>
              <div v-if="!mobile" class="wiz-sub">{{ $t('graphRagWizDropFolderHint') }}</div>
              <div v-if="!mobile" class="wiz-sub">{{ $t('graphRagWizDropFolderHint2') }}</div>
              <div class="wiz-sub">{{ $t('graphRagWizAcceptedTypes') }}</div>
            </div>

            <input ref="fileInput" type="file" multiple hidden :accept="acceptAttr" data-testid="wiz-dropzone-input" @change="onPick" />
            <!-- ไม่ใส่ accept กับ picker ของโฟลเดอร์โดยเจตนา — ไฟล์นามสกุลอื่นต้องถูกรับมาแล้วรายงานว่าข้าม ไม่ใช่หายเงียบ -->
            <input ref="folderInput" type="file" multiple hidden webkitdirectory data-testid="wiz-folder-input" @change="onPickFolder" />

            <!-- ตัวเลือกการนำเข้า -->
            <v-row class="mt-1">
              <v-col cols="12" md="6">
                <v-select
                  :model-value="state.labelRule"
                  :items="labelRules"
                  item-title="title"
                  item-value="value"
                  :label="$t('graphRagWizLabelRule')"
                  :aria-label="$t('graphRagWizLabelRule')"
                  :disabled="!canEdit"
                  density="comfortable"
                  variant="outlined"
                  hide-details
                  data-testid="wiz-label-rule"
                  @update:model-value="patch({ labelRule: $event })"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  :model-value="state.hierarchyDirection"
                  :items="hierarchyDirections"
                  item-title="title"
                  item-value="value"
                  :label="$t('graphRagWizHierarchy')"
                  :aria-label="$t('graphRagWizHierarchy')"
                  :hint="hierarchyRelation"
                  persistent-hint
                  :disabled="!canEdit"
                  density="comfortable"
                  variant="outlined"
                  data-testid="wiz-hierarchy-direction"
                  @update:model-value="patch({ hierarchyDirection: $event })"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  :model-value="state.useParentLineage"
                  color="#C32E0E"
                  density="comfortable"
                  :label="$t('graphRagWizParentLineage')"
                  :hint="$t('graphRagWizParentLineageHint')"
                  persistent-hint
                  :disabled="!canEdit"
                  data-testid="wiz-parent-lineage"
                  @update:model-value="patch({ useParentLineage: !!$event })"
                />
              </v-col>
            </v-row>

            <!-- ผังโฟลเดอร์ — ต้องขึ้นให้เห็นตั้งแต่ก่อนแปลง จึงอ่านจากคิวที่เพิ่งรับเข้ามา ไม่ใช่จากผลแปลง (TC-37/38) -->
            <div v-if="folderMode && folderTree.length" class="wiz-folder-tree mt-4" data-testid="wiz-folder-tree">
              <div class="wiz-sub wiz-folder-tree-head">{{ $t('graphRagWizFolderTreeTitle', { count: state.files.length }) }}</div>
              <div v-for="row in folderTree" :key="row.key" class="wiz-folder-tree-row" :style="{ paddingInlineStart: row.depth * 14 + 'px' }" :data-testid="'wiz-folder-tree-row-' + row.key">
                <span class="wiz-folder-tree-icon">{{ row.icon }}</span>
                <span class="wiz-folder-tree-name">{{ row.name }}</span>
                <span v-if="row.fileCount" class="wiz-sub">{{ $t('graphRagWizFolderFileCount', { count: row.fileCount }) }}</span>
              </div>
            </div>

            <!-- คิวไฟล์ -->
            <div class="d-flex align-center justify-space-between mt-4">
              <strong class="wiz-section">{{ $t('graphRagWizQueueTitle', { count: state.files.length }) }}</strong>
              <v-btn v-if="state.files.length" size="small" variant="text" data-testid="wiz-queue-clear" :disabled="!canEdit" @click="canEdit && $emit('clear-queue')">
                {{ $t('graphRagWizQueueClear') }}
              </v-btn>
            </div>

            <div v-if="!state.files.length" class="wiz-sub mt-2" data-testid="wiz-queue-empty">{{ $t('graphRagWizQueueEmpty') }}</div>

            <v-list v-else density="compact" data-testid="wiz-queue">
              <v-list-item v-for="f in state.files" :key="f.key" :data-testid="'wiz-queue-item-' + f.key" class="wiz-file">
                <template #prepend>
                  <v-progress-circular v-if="f.status === 'converting'" indeterminate color="#C32E0E" size="16" />
                  <v-icon v-else :color="statusColour(f)" :icon="statusIcon(f)" />
                </template>

                <v-list-item-title class="wiz-file-name">{{ f.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  <span>{{ prettySize(f.size) }} · {{ statusText(f) }}</span>
                  <!-- ไฟล์ตารางต้องบอกจำนวนชีตและโหมดที่เลือกไว้ตั้งแต่ในคิว (7-A) -->
                  <span v-if="tabularNote(f)"> · {{ tabularNote(f) }}</span>
                  <span v-if="f.errorText"> · {{ f.errorText }}</span>
                  <v-progress-linear v-if="f.status === 'converting'" :model-value="f.progress" color="#C32E0E" height="4" rounded class="mt-1" :aria-label="$t('graphRagWizStatusConverting')" />
                  <div v-if="f.session && f.session.isDuplicateFile" class="wiz-sub">{{ $t('graphRagWizDuplicateHint') }}</div>
                  <div v-if="f.status === 'unsupported'" class="wiz-sub">{{ unsupportedNote(f) }}</div>
                </v-list-item-subtitle>

                <template #append>
                  <v-btn v-if="canRetry(f)" size="x-small" variant="text" :data-testid="'wiz-queue-retry-' + f.key" :disabled="!canEdit" @click="canEdit && $emit('retry-file', f.key)">
                    {{ $t('graphRagWizFileRetry') }}
                  </v-btn>
                  <v-tooltip :text="$t('graphRagWizFileRemove')">
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon="mdi-close"
                        size="x-small"
                        variant="text"
                        :aria-label="$t('graphRagWizFileRemove')"
                        :data-testid="'wiz-queue-remove-' + f.key"
                        :disabled="!canEdit"
                        @click="canEdit && $emit('remove-file', f.key)"
                      />
                    </template>
                  </v-tooltip>
                </template>
              </v-list-item>
            </v-list>

            <!-- ความคืบหน้า 2 แถบ -->
            <div v-if="state.files.length" class="mt-3" aria-live="polite">
              <div class="wiz-sub">{{ $t('graphRagWizProgressDoc', { done: docDone, total: docTotal }) }}</div>
              <v-progress-linear data-testid="wiz-progress-doc" :model-value="docPct" :aria-valuenow="docPct" :aria-label="$t('graphRagWizProgressDoc', { done: docDone, total: docTotal })" color="#C32E0E" height="6" rounded />
              <div class="wiz-sub mt-2">{{ $t('graphRagWizProgressPage', { done: pageDone, total: pageTotal }) }}</div>
              <v-progress-linear data-testid="wiz-progress-page" :model-value="pagePct" :aria-valuenow="pagePct" :aria-label="$t('graphRagWizProgressPage', { done: pageDone, total: pageTotal })" color="#C32E0E" height="6" rounded />
            </div>

            <!--
              ── ตั้งค่าการนำเข้าตาราง (7-A) ──────────────────────────────
              โผล่เมื่อคิวมีไฟล์ .csv/.xlsx เท่านั้น · แต่ละไฟล์ต้องเลือกก่อนว่าจะอ่านแบบไหน
              ไม่เลือก = ไปขั้นถัดไปไม่ได้ (ลอกจาก mdStep1Next ของ nexa)
            -->
            <div v-if="tabularFiles.length" class="mt-4" data-testid="wiz-tabular-settings">
              <strong class="wiz-section">📊 {{ $t('graphRagWizTabularSettings') }}</strong>
              <!-- OBS-1: โหมดตารางรับแค่ CSV/XLSX — ห้ามยืมข้อความชนิดไฟล์ของการนำเข้าเอกสารมาใช้ -->
              <div class="wiz-sub">{{ $t('graphRagWizTabularSupported') }}</div>

              <v-checkbox
                :model-value="allStructured"
                color="#C32E0E"
                density="comfortable"
                :label="'📐 ' + $t('graphRagWizTableStructured')"
                :hint="$t('graphRagWizTableStructuredHint')"
                persistent-hint
                :disabled="!canEdit || !tabularDoneFiles.length"
                data-testid="wiz-structured-toggle"
                @update:model-value="setAllTableMode($event ? 'structured' : 'standard')"
              />

              <!-- ไฟล์ที่ยังไม่เลือกโหมดต้องกางไว้ ไม่งั้นข้อความเตือนชี้ไปที่ปุ่มที่ยังซ่อนอยู่ในแผงพับ -->
              <v-expansion-panels :model-value="openTabularPanels" multiple variant="accordion" class="mt-3" @update:model-value="onTabularPanels">
                <v-expansion-panel v-for="f in tabularFiles" :key="f.key" :value="f.key" :data-testid="'wiz-tabular-file-' + f.key">
                  <v-expansion-panel-title>
                    <span class="wiz-file-name">{{ f.name }}</span>
                    <span v-if="tabularNote(f)" class="wiz-sub ms-2">{{ tabularNote(f) }}</span>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <v-btn-toggle
                      :model-value="f.tableMode"
                      density="comfortable"
                      color="#C32E0E"
                      :disabled="!canEdit || f.status !== 'done'"
                      :data-testid="'wiz-file-table-mode-' + f.key"
                    >
                      <v-btn value="standard" :data-testid="'wiz-table-standard-' + f.key" @click="setTableMode(f, 'standard')">{{ $t('graphRagWizTableStandard') }}</v-btn>
                      <v-btn value="structured" :data-testid="'wiz-table-structured-' + f.key" @click="setTableMode(f, 'structured')">{{ $t('graphRagWizTableStructured') }}</v-btn>
                    </v-btn-toggle>

                    <div class="wiz-sub mt-1">{{ tableModeHint(f) }}</div>

                    <!-- ตั้งค่ารายชีต — ชีตที่ไม่ติ๊กจะไม่ถูกนำเข้า -->
                    <div v-if="sheetsBusy(f)" class="wiz-sub mt-3">{{ $t('graphRagWizSheetsLoading') }}</div>
                    <div v-else-if="!sheetsOf(f).length" class="wiz-sub mt-3" :data-testid="'wiz-sheets-empty-' + f.key">{{ $t('graphRagWizNoSheets') }}</div>
                    <div v-else class="mt-2" :data-testid="'wiz-sheets-' + f.key">
                      <v-checkbox
                        v-for="s in sheetsOf(f)"
                        :key="s.key"
                        :model-value="s.include !== false"
                        color="#C32E0E"
                        density="compact"
                        hide-details
                        :label="s.label || s.sheetName"
                        :aria-label="$t('graphRagWizSheetInclude')"
                        :disabled="!canEdit"
                        :data-testid="'wiz-sheet-include-' + s.key"
                        @update:model-value="setSheetInclude(s, !!$event)"
                      />
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <v-alert v-if="state.tooMany" type="warning" variant="tonal" density="compact" class="mt-3" aria-live="polite">
              {{ $t('graphRagWizTooManyFiles', { max: maxFiles }) }}
            </v-alert>

            <!-- ข้อความผิดพลาดของขั้น 1 มีจุดเดียว — ไฟล์ที่ข้าม + ไฟล์ตารางที่ยังไม่เลือกโหมด รวมอยู่ที่นี่ -->
            <v-alert v-if="step1Errors.length" type="error" variant="tonal" density="compact" class="mt-3" role="alert" data-testid="wiz-step1-error">
              <div v-for="row in step1Errors" :key="row.key">{{ row.text }}</div>
            </v-alert>

            <v-alert v-if="state.sessionExpired" type="error" variant="tonal" density="compact" class="mt-3" role="alert">
              {{ $t('graphRagWizSessionExpired') }}
            </v-alert>
          </v-stepper-window-item>

          <!-- ── ขั้น 2 ตรวจเนื้อหา (5-A) ─────────────────────────────── -->
          <v-stepper-window-item :value="2">
            <ImportWizardStep2 :state="state" :graph-key="graphKey" :can-edit="canEdit" :mobile="mobile" @patch="patch" @notify="$emit('notify', $event)" @goto="goto" />
          </v-stepper-window-item>

          <!-- ── ขั้น 3 วันที่ (5-B) ──────────────────────────────────── -->
          <v-stepper-window-item :value="3">
            <ImportWizardStep3 :state="state" :graph-key="graphKey" :can-edit="canEdit" :mobile="mobile" @patch="patch" @notify="$emit('notify', $event)" @goto="goto" />
          </v-stepper-window-item>

          <!-- ── ขั้น 4 จับคู่ระดับ (5-C) ─────────────────────────────── -->
          <v-stepper-window-item :value="4">
            <ImportWizardStep4 :state="state" :graph-key="graphKey" :can-edit="canEdit" :mobile="mobile" @patch="patch" @notify="$emit('notify', $event)" @goto="goto" />
          </v-stepper-window-item>

          <!-- ── ขั้น 5 ตรวจและสร้าง ──────────────────────────────────── -->
          <v-stepper-window-item :value="5">
            <template v-if="previewEmpty">
              <div class="wiz-sub" data-testid="wiz-preview-empty">{{ $t('graphRagWizPreviewEmpty') }}</div>
              <v-btn class="mt-2" size="small" variant="outlined" data-testid="wiz-back-to-upload" @click="goto(1)">{{ $t('graphRagWizBackToUpload') }}</v-btn>
            </template>

            <template v-else>
              <!-- ก่อนนำเข้า ตัวเลขจริงยังไม่มี — ห้ามแสดง 0 หลอกผู้ใช้ (มติ ISSUE-3) · นำเข้าแล้วตัวเลขจริงอยู่ในกล่องผลด้านล่าง -->
              <div v-if="!state.applyResult" class="wiz-section" data-testid="wiz-preview-summary">
                <template v-if="estimate">{{ $t('graphRagWizPreviewEstimate', { nodes: estimate.nodes, edges: estimate.edges, files: doneFiles.length }) }}</template>
                <template v-else>{{ $t('graphRagWizPreviewUnknown', { files: doneFiles.length }) }}</template>
              </div>

              <div v-if="state.parentNodeKey" class="wiz-sub mt-2" data-testid="wiz-parent-note">
                {{ $t('graphRagWizUnderNode', { node: state.parentNodeKey }) }}
              </div>

              <!-- ต้นไม้พรีวิว: v-list + v-list-group ซ้อน (DEV-1 — ไม่เปิด Vuetify labs) -->
              <v-list density="compact" class="wiz-tree mt-2" data-testid="wiz-preview-tree">
                <v-list-group v-for="g in previewGroups" :key="g.key" :value="g.key">
                  <template #activator="{ props }">
                    <v-list-item v-bind="props" :title="g.title" :subtitle="g.subtitle" :data-testid="'wiz-preview-group-' + g.key" />
                  </template>
                  <v-list-item v-for="c in g.children" :key="c.key" :title="c.title" :subtitle="c.subtitle" :data-testid="'wiz-preview-leaf-' + c.key" />
                </v-list-group>
              </v-list>

              <!--
                ✏️ 2026-10-19 ค่าเริ่มต้น = Answer model ของกราฟบน toolbar (GraphEditor.wizSyncModel) · เปลี่ยนได้เฉพาะการนำเข้ารอบนี้
                (ไม่บันทึกกลับเป็น Answer model — ช่องนั้นใช้ตอบคำถามด้วย) · hint บอกเสมอว่าค่านี้มาจากไหน (มีผลต่อค่า token)
                ตารางแบบกำหนดเองล้วนไม่เรียกโมเดล ⇒ ไม่แสดง
              -->
              <!-- พิมพ์ค้นได้ทั้งเลข model id และชื่อ · คลิกแล้วเลือกข้อความทั้งช่อง พิมพ์ทับได้ทันที (เหมือนช่อง Answer model บน toolbar) -->
              <v-autocomplete
                v-if="needsModel"
                ref="modelSelect"
                :model-value="state.modelId"
                :items="modelItems"
                :no-data-text="$t('noDataAvailable')"
                :label="$t('graphRagWizModel')"
                :aria-label="$t('graphRagWizModel')"
                :hint="modelHint"
                persistent-hint
                :disabled="!canEdit || !state.models.length || state.creating || state.applied"
                prepend-inner-icon="mdi-robot"
                density="compact"
                variant="outlined"
                class="wiz-model mt-3"
                data-testid="wiz-model"
                @update:model-value="$event != null && patch({ modelId: $event, modelPicked: true })"
                @update:focused="selectModelText"
                @keydown="onModelKeydown($event, modelTitle)"
              />

              <!-- ✏️ 2026-10-19 ติ๊ก = แตกโหนดเสร็จแล้วเปิดหน้าข้อเสนอให้ Reject ก่อน · ไม่ติ๊ก = นำเข้าทันทีแบบ nexa -->
              <v-checkbox
                v-if="needsModel"
                :model-value="state.reviewBeforeApply"
                :label="$t('graphRagWizReviewFirst')"
                :hint="$t('graphRagWizReviewFirstHint')"
                persistent-hint
                :disabled="!canEdit || state.creating || state.applied || jobReady"
                color="#C32E0E"
                density="compact"
                class="mt-2"
                data-testid="wiz-review-first"
                @update:model-value="patch({ reviewBeforeApply: !!$event })"
              />
              <!-- ตรวจข้อเสนอแล้ว ยังไม่ได้สร้าง — สรุปให้เห็นก่อนกด Create nodes · เปลี่ยนโมเดลหลังตรวจ = ต้องแตกโหนดใหม่ -->
              <v-alert v-if="modelChangedSinceJob" type="warning" variant="tonal" density="compact" class="mt-3" aria-live="polite" data-testid="wiz-model-changed">
                {{ $t('graphRagWizModelChangedAfterReview') }}
              </v-alert>
              <v-alert v-else-if="reviewedAndReady" type="info" color="#C32E0E" variant="tonal" density="compact" class="mt-3" aria-live="polite" data-testid="wiz-review-summary">
                <div v-if="state.reviewSummary">
                  {{ $t('graphRagWizReviewSummary', { nodes: state.reviewSummary.nodes, edges: state.reviewSummary.edges, rejected: state.reviewSummary.rejected }) }}
                </div>
                <div v-else>{{ $t('graphRagWizReviewReady') }}</div>
                <v-btn class="mt-2" size="small" variant="outlined" prepend-icon="mdi-format-list-checks" data-testid="wiz-edit-review" @click="patch({ showProposals: true })">
                  {{ $t('graphRagWizEditReview') }}
                </v-btn>
              </v-alert>

              <!-- ความคืบหน้า 4 ขั้น — div + v-icon (DEV-2 · ห้ามซ้อน v-stepper ใน v-stepper) -->
              <div class="wiz-prog mt-3" aria-live="polite">
                <div v-for="p in progSteps" :key="p.value" class="wiz-prog-item" :class="{ 'wiz-prog-item--on': shownProgStep >= p.value }" :data-testid="'wiz-prog-' + p.value">
                  <v-icon size="16" :color="shownProgStep >= p.value ? '#C32E0E' : '#646464'" :icon="shownProgStep >= p.value ? 'mdi-check-circle' : 'mdi-circle-outline'" />
                  <span>{{ p.title }}</span>
                </div>
              </div>
              <div class="wiz-sub" data-testid="wiz-prog-detail">{{ progDetail }}</div>

              <div v-if="jobRunning" class="mt-2">
                <v-btn size="small" variant="text" color="error" data-testid="wiz-cancel-job" @click="$emit('cancel-job')">{{ $t('graphRagWizCancelJob') }}</v-btn>
              </div>

              <v-alert v-if="state.duplicateJob" type="info" color="#C32E0E" variant="tonal" density="compact" class="mt-3" aria-live="polite">
                {{ $t('graphRagWizJobBusy') }}
              </v-alert>

              <v-alert v-if="state.createError" type="error" variant="tonal" density="compact" class="mt-3" role="alert" data-testid="wiz-step5-error">
                {{ state.createError }}
              </v-alert>

              <!-- นำเข้าอัตโนมัติแล้วแต่มีรายการถูกข้าม (ไม่มีคำเตือน = wizard ปิดเองไปแล้ว ไม่มาถึงตรงนี้) -->
              <v-alert v-if="state.applyResult" :type="applyWarnings.length ? 'warning' : 'success'" variant="tonal" density="compact" class="mt-3" aria-live="polite" data-testid="wiz-apply-result">
                <div>
                  {{ $t('graphRagProposalAppliedSummary', { nodes: state.applyResult.nodeCount, edges: state.applyResult.edgeCount }) }}
                  <span v-if="state.applyResult.versionNo"> · {{ $t('graphRagVersion') }} v{{ state.applyResult.versionNo }}</span>
                </div>
                <ul v-if="applyWarnings.length" class="wiz-apply-warnings mt-1" data-testid="wiz-apply-warnings">
                  <li v-for="(w, i) in applyWarnings" :key="i">{{ w }}</li>
                </ul>
                <v-btn class="mt-2" size="small" variant="outlined" prepend-icon="mdi-format-list-checks" data-testid="wiz-open-proposals" @click="patch({ showProposals: true })">
                  {{ $t('graphRagWizOpenProposals') }}
                </v-btn>
              </v-alert>
            </template>

            <ProposalReview
              :model-value="state.showProposals"
              :graph-key="graphKey"
              :job-id="state.jobId"
              :can-edit="canEdit"
              :review-only="state.reviewBeforeApply && !state.applied"
              @update:model-value="patch({ showProposals: $event })"
              @applied="$emit('applied', $event)"
              @notify="$emit('notify', $event)"
            />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
    </v-card-text>

    <!-- แถบปุ่มตำแหน่งคงที่ทุกขั้น — ขั้น 1 เว้นที่ปุ่มย้อนกลับไว้ ปุ่มขวาจะได้ไม่ขยับ -->
    <v-card-actions class="wiz-actions">
      <v-btn v-if="state.step > 1" variant="text" :data-testid="'wiz-step' + state.step + '-back'" @click="goBack">{{ $t('graphRagWizBack') }}</v-btn>
      <span v-else class="wiz-back-hole" />
      <v-spacer />
      <!-- นำเข้าแล้ว = ปุ่มนี้คือ "เสร็จสิ้น" (ปิดแล้วไม่ต้องถามยืนยัน) และไม่มีปุ่มสร้างซ้ำให้กดอีก -->
      <v-btn
        :variant="state.applied ? 'flat' : 'text'"
        :color="state.applied ? '#C32E0E' : undefined"
        data-testid="wiz-cancel"
        @click="$emit('close')"
      >
        {{ state.applied ? $t('graphRagProposalDone') : $t('cancel') }}
      </v-btn>

      <!-- v-btn ที่ disabled ไม่ยิง pointer event ⇒ ตัวรับ hover ต้องเป็น span ที่ครอบปุ่ม ไม่ใช่ตัวปุ่มเอง -->
      <v-tooltip v-if="state.step < 5" :text="nextTooltip" :disabled="!nextDisabled">
        <template #activator="{ props }">
          <span v-bind="props" class="wiz-tip-wrap">
            <v-btn v-if="showNext" color="#C32E0E" :data-testid="'wiz-step' + state.step + '-next'" :disabled="nextDisabled" @click="!nextDisabled && goNext()">
              {{ $t('graphRagWizNext') }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>

      <v-tooltip v-else-if="!state.applied" :text="createTooltip" :disabled="!createDisabled">
        <template #activator="{ props }">
          <span v-bind="props" class="wiz-tip-wrap">
            <v-btn color="#C32E0E" data-testid="wiz-create" :loading="state.creating" :disabled="createDisabled" @click="!createDisabled && $emit('create')">
              {{ createLabel }}
            </v-btn>
          </span>
        </template>
      </v-tooltip>
    </v-card-actions>
  </div>
</template>

<script>
import ImportWizardStep2 from '@/components/GraphRAG/ImportWizardStep2.vue';
import ImportWizardStep3 from '@/components/GraphRAG/ImportWizardStep3.vue';
import ImportWizardStep4 from '@/components/GraphRAG/ImportWizardStep4.vue';
import ProposalReview from '@/components/GraphRAG/ProposalReview.vue';

const CONST_WIZ_ACCEPT = '.md,.pdf,.docx,.txt,.csv,.xlsx,.pptx';
/** โหมดตารางรองรับสองนามสกุลนี้เท่านั้น (OBS-1 — ห้ามยืมลิสต์ชนิดไฟล์ของการนำเข้าเอกสารมาโฆษณา) */
const CONST_WIZ_TABULAR_EXT = ['.csv', '.xlsx'];
/**
 * นามสกุลรุ่นเก่าที่ข้อความ graphRagUnsupportedNote พูดถึงจริง ๆ (FIX-4)
 * ไฟล์นอกลิสต์นี้ (เช่น .json) ต้องได้ข้อความที่ตรงเคสของตัวเอง ไม่ใช่คำอธิบายของ .doc/.ppt
 */
const CONST_WIZ_LEGACY_EXT = ['.doc', '.ppt', '.xls'];
/** ต้องตรงกับ CONST_WIZ_MAX_BYTES ของ views/GraphEditor.vue และ CONST_MAX_UPLOAD_BYTES ของ controller */
const CONST_WIZ_MAX_BYTES = 50 * 1024 * 1024;
/** ระดับ heading ลึกสุดที่ Markdown รองรับ — ใช้ตอนประเมินจำนวนโหนดฝั่ง client */
const CONST_WIZ_MAX_HEADING_LEVEL = 6;

export default {
  name: 'ImportWizard',
  components: { ImportWizardStep2, ImportWizardStep3, ImportWizardStep4, ProposalReview },
  props: {
    /** สถานะกลางที่ views/GraphEditor.vue เป็นเจ้าของ (D10) — อ่านอย่างเดียว */
    state: { type: Object, required: true },
    graphKey: { type: String, required: true },
    canEdit: { type: Boolean, default: false },
    maxFiles: { type: Number, default: 100 },
  },
  emits: ['patch', 'notify', 'switch-mode', 'add-files', 'remove-file', 'retry-file', 'clear-queue', 'create', 'cancel-job', 'close', 'applied'],
  data() {
    return {
      dragOver: false,
      /**
       * ตรวจครั้งเดียวตอนสร้าง component — ต้องรู้ "ก่อน" render ปุ่มโหมด เพราะเบราว์เซอร์ที่ไม่รองรับ
       * ต้องไม่มีปุ่มโหมดโฟลเดอร์เลย (§Folder Mode · TC-46) ไม่ใช่มีปุ่มที่กดแล้วเงียบ
       */
      folderSupported: false,
      /**
       * แผงตั้งค่าตารางที่กางอยู่ (7-A) — null = ยังไม่ได้แตะเอง ให้ระบบกางไฟล์ที่ยังไม่เลือกโหมดให้
       * เป็นสถานะหน้าจอชั่วคราวล้วน ไม่ใช่ข้อมูลของงาน จึงเก็บที่นี่ได้ ไม่ขัดมติ D10
       */
      tabularPanels: null,
    };
  },
  created() {
    this.folderSupported = 'webkitdirectory' in document.createElement('input');
  },
  computed: {
    mobile() {
      return this.$vuetify.display.smAndDown;
    },
    folderMode() {
      return this.state.mode === 'folder';
    },
    /** ระหว่างแปลง/กำลังสร้าง ห้ามสลับโหมด เพราะการสลับล้างคิวทิ้งทั้งชุด (TC-45) */
    modeLocked() {
      return !!this.state.converting || !!this.state.creating;
    },
    /**
     * ผังโฟลเดอร์แบบแบนพร้อมความลึก — พอร์ตจาก buildFolderTreeFromEntries + renderFolderTree ของ nexa
     * โฟลเดอร์ที่มีลูกใช้ 📁 ไม่มีลูกใช้ 📂 · คีย์ของแถวมาจาก path ไม่ใช่ index ของอาร์เรย์
     */
    folderTree() {
      if (!this.folderMode) return [];

      const root = { children: [], fileCount: 0 };
      const index = {};

      (this.state.files || []).forEach((file) => {
        const path = file.folderPath || '';
        if (!path) return;

        const parts = path.split('/');
        let node = root;

        parts.forEach((name, depth) => {
          const sub = parts.slice(0, depth + 1).join('/');

          if (!index[sub]) {
            index[sub] = { name: name, path: sub, depth: depth, children: [], fileCount: 0 };
            node.children.push(index[sub]);
          }

          node = index[sub];
        });

        node.fileCount += 1;
      });

      const rows = [];
      const walk = (node) => {
        node.children.forEach((child) => {
          rows.push({ key: this.slug(child.path), name: child.name, depth: child.depth, fileCount: child.fileCount, icon: child.children.length ? '📁' : '📂' });
          walk(child);
        });
      };

      walk(root);

      return rows;
    },
    acceptAttr() {
      return CONST_WIZ_ACCEPT;
    },
    steps() {
      return [
        { value: 1, title: this.$t('graphRagWizStep1') },
        { value: 2, title: this.$t('graphRagWizStep2') },
        { value: 3, title: this.$t('graphRagWizStep3') },
        { value: 4, title: this.$t('graphRagWizStep4') },
        { value: 5, title: this.$t('graphRagWizStep5') },
      ];
    },
    stepHeading() {
      const found = this.steps.find((s) => s.value === this.state.step);

      return this.$t('graphRagWizStepOf', { n: this.state.step, name: found ? found.title : '' });
    },
    labelRules() {
      return [
        { value: 'none', title: this.$t('graphRagWizLabelRuleNone') },
        { value: 'stripDate', title: this.$t('graphRagWizLabelRuleDate') },
        { value: 'stripLang', title: this.$t('graphRagWizLabelRuleDateLang') },
      ];
    },
    hierarchyDirections() {
      return [
        { value: 'bottom_up', title: this.$t('graphRagWizHierarchyBottomUp') },
        { value: 'top_down', title: this.$t('graphRagWizHierarchyTopDown') },
      ];
    },
    /** ชื่อ relation อยู่ใน hint ไม่ใช่ใน item-title (กติกา §Component Mapping) */
    hierarchyRelation() {
      return this.state.hierarchyDirection === 'top_down' ? 'contains_section' : 'belongs_to_section';
    },
    editableMarkdownFiles() {
      return (this.state.files || []).filter((f) => f.status === 'done' && f.ext !== '.csv' && f.ext !== '.xlsx');
    },
    // ── โหมดตาราง CSV/XLSX (7-A) ────────────────────────────────────────────
    tabularFiles() {
      return (this.state.files || []).filter((f) => CONST_WIZ_TABULAR_EXT.includes(f.ext));
    },
    /** เลือกโหมดได้ก็ต่อเมื่อแปลงเสร็จแล้ว เพราะต้องมี session ไว้อ่านรายชื่อชีต */
    tabularDoneFiles() {
      return this.tabularFiles.filter((f) => f.status === 'done' && f.session);
    },
    needTableModeFiles() {
      return this.tabularDoneFiles.filter((f) => !f.tableMode);
    },
    structuredFiles() {
      return this.tabularDoneFiles.filter((f) => f.tableMode === 'structured');
    },
    allStructured() {
      return this.tabularDoneFiles.length > 0 && this.tabularDoneFiles.every((f) => f.tableMode === 'structured');
    },
    /** งานอื่นที่ไม่ใช่ตารางแบบกำหนดเอง = ไฟล์ markdown, ตารางทั่วไป หรืออยู่โหมดโฟลเดอร์ */
    hasOtherWork() {
      return this.folderMode || this.doneFiles.some((f) => !this.isStructuredFile(f));
    },
    /** เส้นทาง StartExtract เท่านั้นที่ต้องใช้โมเดล — ตารางแบบกำหนดเองไม่เรียก LLM เลย */
    needsModel() {
      return this.doneFiles.some((f) => !this.isStructuredFile(f));
    },
    /** ชื่อตัวที่เลือกอยู่ตามที่ช่องแสดง — onModelKeydown ใช้รู้ว่าข้อความในช่องยังไม่ถูกแก้ */
    modelTitle() {
      const hit = this.modelItems.find((i) => i.value === this.state.modelId);

      return hit ? hit.title : '';
    },
    /** ป้ายเดียวกับช่อง Answer model บน toolbar: "{ModelDisplay} {ชื่อ}" */
    modelItems() {
      return this.state.models.map((m) => ({ title: `${m.modelDisplay ?? ''} ${m.name}`.trim(), value: m.id }));
    },
    /** บอกว่าโมเดลที่จะใช้มาจากไหน — ตรงกับกราฟ / กราฟไม่ได้ตั้ง (จะตั้งให้) / เลือกเองเฉพาะรอบนี้ / ตัวที่ตั้งไม่อยู่ในลิสต์จึงใช้ตัวแรก */
    modelHint() {
      if (!this.state.models.length) return this.$t('graphRagWizNoModel');
      if (this.state.graphModelId != null && this.state.modelId === this.state.graphModelId) return this.$t('graphRagWizModelInUse');
      // กราฟยังไม่ได้ตั้ง ⇒ นำเข้าสำเร็จแล้วโมเดลนี้จะกลายเป็น Answer model (GraphEditor.wizAdoptAnswerModel) — บอกล่วงหน้า
      if (!this.state.graphModelSet) return this.$t('graphRagWizModelWillAdopt');
      if (this.state.modelPicked) return this.$t('graphRagWizModelOverride');

      return this.$t('graphRagWizModelFallback');
    },
    structuredSheetRows() {
      const keys = this.structuredFiles.map((f) => f.key);

      return (this.state.structuredSheets || []).filter((s) => keys.indexOf(s.fileKey) >= 0);
    },
    readySheets() {
      return this.structuredSheetRows.filter((s) => s.include !== false && s.mapping && s.mapping.nodeKey);
    },
    unconfiguredSheets() {
      return this.structuredSheetRows.filter((s) => s.include !== false && !(s.mapping && s.mapping.nodeKey));
    },
    /** ข้อความผิดพลาดทั้งหมดของขั้น 1 — แสดงในกล่องเดียว (testid ต้องมีจุดเดียว) */
    step1Errors() {
      const rows = [];

      if (this.state.skipped.length) rows.push({ key: 'skipped', text: this.$t('graphRagWizSkippedFiles', { count: this.state.skipped.length, names: this.state.skipped.join(', ') }) });
      this.needTableModeFiles.forEach((f) => rows.push({ key: 'mode-' + f.key, text: this.$t('graphRagWizNeedTableMode', { file: f.name }) }));

      return rows;
    },
    openTabularPanels() {
      if (this.tabularPanels) return this.tabularPanels;

      return this.needTableModeFiles.map((f) => f.key);
    },
    /** ลายเซ็นของไฟล์ตารางที่แปลงเสร็จ — ใช้จุด watcher ให้ไปอ่านรายชื่อชีต */
    tabularSignature() {
      return this.tabularDoneFiles.map((f) => f.key).join('|');
    },
    docTotal() {
      return this.state.files.length;
    },
    docDone() {
      return this.state.files.filter((f) => f.status !== 'pending' && f.status !== 'converting').length;
    },
    docPct() {
      return this.docTotal ? Math.round((this.docDone / this.docTotal) * 100) : 0;
    },
    /** backend ไม่ส่งความคืบหน้ารายหน้ามา — ใช้จำนวนหน้าที่รู้แล้วเป็นค่าต่ำสุดที่เพิ่มขึ้นเรื่อย ๆ */
    pageDone() {
      return this.state.files.reduce((sum, f) => sum + (f.session ? f.session.pageCount || 1 : 0), 0);
    },
    pageTotal() {
      const rest = this.state.files.filter((f) => !f.session).length;

      return this.pageDone + rest;
    },
    pagePct() {
      return this.pageTotal ? Math.round((this.pageDone / this.pageTotal) * 100) : 0;
    },
    doneFiles() {
      return this.state.files.filter((f) => f.status === 'done');
    },
    applyWarnings() {
      return (this.state.applyResult && this.state.applyResult.warnings) || [];
    },
    /**
     * ประมาณการฝั่ง client — ใช้ได้เฉพาะเส้นทาง deterministic ของ HeadingNodeBuilder (มี levelMapping)
     * เส้นทาง LLM เดาจำนวนโหนดไม่ได้ ⇒ คืน null แล้วให้ UI บอกว่าจะรู้หลังวิเคราะห์เอกสาร
     *
     * นับตามกติกาเดียวกับ HeadingNodeBuilder.BuildItems: โหนดรากของไฟล์ (ระดับ 0) + หัวข้อทุกตัว
     * ระดับที่ผู้ใช้ตั้งชนิดเป็นค่าว่าง = ข้าม · ระดับที่ไม่ได้ระบุใช้ชนิดมาตรฐาน = ไม่ข้าม
     */
    estimate() {
      const mapping = this.state.levelMapping || [];
      if (!mapping.length) return null;

      const skipped = {};
      mapping.forEach((row) => {
        if (row) skipped[row.level] = !String(row.nodeType || '').trim();
      });

      let nodes = 0;
      let edges = 0;

      this.doneFiles.forEach((f) => {
        const one = this.estimateFile(f.session ? f.session.markdown : '', skipped);
        nodes += one.nodes;
        edges += one.edges;
      });

      return { nodes: nodes, edges: edges };
    },
    previewEmpty() {
      return !this.doneFiles.length;
    },
    /**
     * จัดกลุ่มตามไฟล์ — หัวข้อที่อ่านได้จาก markdown ที่แปลงแล้ว ต่อด้วยคำเตือนของไฟล์
     * (เดิมหลังงานจบสลับไปจัดกลุ่มตามข้อเสนอจริง แต่ตอนนี้งานจบ = นำเข้าทันที ไม่มีจังหวะให้ดูข้อเสนอก่อนแล้ว)
     */
    previewGroups() {
      return this.doneFiles.map((f) => {
        const warnings = f.session && f.session.warnings ? f.session.warnings : [];
        const children = this.uniqueChildren(f.key + '-h', this.parseHeadings(f.session ? f.session.markdown : '').map((h) => ({ title: h.text, subtitle: 'H' + h.level })));

        return {
          key: f.key,
          title: f.name,
          subtitle: f.session ? f.session.sourceType : '',
          children: children.concat(this.uniqueChildren(f.key + '-w', warnings.map((w) => ({ title: w, subtitle: '' })))),
        };
      });
    },
    progSteps() {
      return [
        { value: 1, title: this.$t('graphRagWizProg1') },
        { value: 2, title: this.$t('graphRagWizProg2') },
        { value: 3, title: this.$t('graphRagWizProg3') },
        { value: 4, title: this.$t('graphRagWizProg4') },
      ];
    },
    progDetail() {
      // ตอนกำลังส่งยังไม่มีตัวเลขจริง — มีประมาณการค่อยบอกตัวเลข ไม่มีก็บอกแค่ว่ากำลังส่ง
      if (this.state.progStep === 1) return this.estimate ? this.$t('graphRagWizProgSending', { nodes: this.estimate.nodes, edges: this.estimate.edges }) : this.$t('graphRagWizProg1');
      if (this.state.progStep === 2) return this.$t('graphRagWizProg2');
      if (this.state.progStep === 3) return this.$t('graphRagWizProgSaving');
      if (this.reviewedAndReady) return this.$t('graphRagWizProgReviewed');
      if (this.state.progStep === 4) return this.$t('graphRagWizProg4');

      return '';
    },
    jobRunning() {
      return !!this.state.job && (this.state.job.status === 'queued' || this.state.job.status === 'running');
    },
    showNext() {
      // ต้นทางซ่อนปุ่มถัดไปจนกว่าจะมีไฟล์ในคิว (ไม่ใช่ disable)
      return this.state.step > 1 || this.state.files.length > 0;
    },
    nextDisabled() {
      // ไฟล์ตารางที่ยังไม่เลือกโหมด = ไปต่อไม่ได้ (ลอกจาก mdStep1Next ของ nexa)
      if (this.state.step === 1) return this.state.converting || !this.doneFiles.length || this.needTableModeFiles.length > 0;
      if (this.state.step === 2) return !!this.state.step2Blocked || !!this.state.step2Busy;
      if (this.state.step === 3) return !!this.state.step3Blocked;
      if (this.state.step === 4) return !!this.state.step4Blocked || !!this.state.step4Busy;

      return false;
    },
    /** ปุ่มที่ถูก disable ต้องบอกเหตุผลได้เสมอ (§Done-When) — ขั้นที่ลูกไม่ส่งเหตุผลมาต้องมีข้อความสำรอง */
    nextTooltip() {
      if (this.state.step === 1) return this.step1Tooltip;
      if (this.state.step === 2) return this.state.step2BlockReason || this.$t('graphRagWizStatusConverting');
      if (this.state.step === 3) return this.state.step3BlockReason || this.$t('graphRagWizNextBlocked');
      if (this.state.step === 4) return this.state.step4BlockReason || this.$t('graphRagWizNextBlocked');

      return this.$t('graphRagWizNextBlocked');
    },
    /** เหตุผลที่ปุ่มถัดไปของขั้น 1 ถูกปิด — ไฟล์ตารางที่ยังไม่เลือกโหมดต้องบอกชื่อไฟล์ให้ครบ */
    step1Tooltip() {
      if (this.state.converting) return this.$t('graphRagWizStatusConverting');
      if (this.needTableModeFiles.length) return this.$t('graphRagWizNeedTableMode', { file: this.needTableModeFiles.map((f) => f.name).join(', ') });

      return this.$t('graphRagWizQueueEmpty');
    },
    /**
     * ป้ายปุ่ม Create 4 แบบ — ลำดับตรวจต้องตรงกับ updateMdCreateButtonLabel (nexa index.html:19846)
     * 1 มีตารางแบบกำหนดเอง + งานอื่น → เริ่มนำเข้า
     * 2 มีแต่ตารางแบบกำหนดเอง → สร้างโหนดจากตาราง
     * 3 โหมดนำเข้า = update → อัปเดตของเดิม
     * 4 นอกนั้น → สร้างโหนด
     * (คำนวณใหม่ทุกครั้งที่ state เปลี่ยน เพราะเป็น computed ไม่ใช่ค่าที่เก็บไว้ตอนเปิด)
     */
    createLabel() {
      if (this.state.creating) return this.state.importMode === 'update' ? this.$t('graphRagWizUpdating') : this.$t('graphRagWizCreating');
      if (this.hasReviewableJob) return this.$t('graphRagWizReviewProposals');
      // โหมดตรวจก่อนสร้าง: ยังไม่ได้ดู (หรือเปลี่ยนโมเดลหลังดู) = ปุ่มนี้แค่แตกโหนดให้ดู ยังไม่สร้าง
      if (this.needsModel && this.state.reviewBeforeApply && !this.reviewedAndReady) return this.$t('graphRagWizPreviewNodes');
      if (this.structuredFiles.length && this.hasOtherWork) return this.$t('graphRagWizRunImport');
      if (this.structuredFiles.length) return this.$t('graphRagWizCreateStructured');
      if (this.state.importMode === 'update') return this.$t('graphRagWizApplyUpdates');

      return this.$t('graphRagWizCreateNodes');
    },
    /** งานแตกโหนดรอบนี้เดินจบแล้ว แต่ยังไม่ได้เขียนลงกราฟ */
    jobReady() {
      return !!this.state.jobId && this.state.progStep === 4 && !this.state.applied && !this.state.creating;
    },
    /**
     * โหมดนำเข้าทันที แต่นำเข้าอัตโนมัติไม่สำเร็จ
     * ⇒ ปุ่มหลักต้องพาไปที่ข้อเสนอชุดเดิมให้นำเข้าเอง ไม่ใช่ยิงงานใหม่ซ้ำจากไฟล์ชุดเดิม
     */
    hasReviewableJob() {
      return this.jobReady && !this.state.reviewBeforeApply;
    },
    /** ข้อเสนอมาจากโมเดลตอนแตกโหนด — เปลี่ยนโมเดลหลังจากนั้น ข้อเสนอชุดเดิมใช้ไม่ได้ ต้องแตกใหม่ */
    modelChangedSinceJob() {
      return this.jobReady && this.state.reviewBeforeApply && this.state.jobModelId != null && this.state.modelId !== this.state.jobModelId;
    },
    /** โหมดตรวจก่อนสร้าง: แตกโหนดแล้ว ตรวจแล้ว โมเดลไม่เปลี่ยน ⇒ กด Create nodes = เขียนลงกราฟ */
    reviewedAndReady() {
      return this.jobReady && this.state.reviewBeforeApply && !this.modelChangedSinceJob;
    },
    /** โหมดตรวจ: แตกโหนดเสร็จแต่ยังไม่สร้าง ⇒ แถบความคืบหน้าค้างที่ขั้นแรก ไม่ติ๊ก "บันทึกกราฟ" ทั้งที่ยังไม่ได้บันทึก */
    shownProgStep() {
      return this.reviewedAndReady || this.modelChangedSinceJob ? 1 : this.state.progStep;
    },
    createDisabled() {
      if (this.hasReviewableJob) return !this.canEdit;
      if (!this.canEdit || this.state.creating || this.previewEmpty) return true;
      // ชีตที่ยังไม่ได้จับคู่คอลัมน์ = ยิงนำเข้าไม่ได้ (ขั้น 4 ห้ามบล็อก จึงมากันที่ปุ่มสร้าง)
      if (this.unconfiguredSheets.length) return true;
      if (this.needsModel && (!this.state.models.length || !this.state.modelId)) return true;

      return !this.needsModel && !this.readySheets.length;
    },
    createTooltip() {
      if (!this.canEdit) return this.$t('graphRagReadOnly');
      if (this.previewEmpty) return this.$t('graphRagWizNothingToCreate');
      if (this.unconfiguredSheets.length) return this.$t('graphRagWizNeedSheetConfig', { file: this.unconfiguredSheets[0].fileName, sheet: this.unconfiguredSheets[0].label || this.unconfiguredSheets[0].sheetName });
      if (this.needsModel && (!this.state.models.length || !this.state.modelId)) return this.$t('graphRagWizNoModel');
      if (!this.needsModel && !this.readySheets.length) return this.$t('graphRagWizNothingToCreate');

      return this.$t('graphRagWizNextBlocked');
    },
  },
  watch: {
    'state.step': function onStep() {
      this.$nextTick(() => {
        if (this.$refs.stepHeading) this.$refs.stepHeading.focus();
      });
    },
    /** ไฟล์ตารางแปลงเสร็จเมื่อไหร่ = อ่านรายชื่อชีตของมันตอนนั้น ผู้ใช้จะได้ตั้งค่ารายชีตได้ทันที */
    tabularSignature: {
      immediate: true,
      handler() {
        this.loadAllSheets();
      },
    },
  },
  methods: {
    patch(fields) {
      this.$emit('patch', fields);
    },
    /** ตัดอักขระที่ใช้ใน selector ไม่ได้ออก แต่คงตัวอักษร/ตัวเลขทุกภาษาไว้ เพื่อไม่ให้ชื่อไทยชนกันเป็นคีย์เดียว */
    slug(text) {
      return String(text || '').replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '') || 'x';
    },
    /** คีย์ของใบไม้มาจากเนื้อหา ไม่ใช่ index ของ array — หัวข้อชื่อซ้ำในไฟล์เดียวกันต่อท้ายด้วยลำดับที่พบ */
    uniqueChildren(prefix, rows) {
      const seen = {};

      return rows.map((row) => {
        const base = this.slug(row.title);
        seen[base] = (seen[base] || 0) + 1;

        return { key: prefix + '-' + base + (seen[base] > 1 ? '-' + seen[base] : ''), title: row.title, subtitle: row.subtitle };
      });
    },
    /**
     * อ่านหัวข้อ ATX ให้ตรงกับ MarkdownChunker.IsAtxHeadingLine — `#` 1–6 ตัวแล้วต้องตามด้วยช่องว่าง
     * (กัน `#hashtag`) · บรรทัดในบล็อกโค้ด ``` ไม่นับ เหมือน HeadingNodeBuilder.ParseHeadings
     */
    parseHeadings(markdown) {
      const result = [];
      let inCodeBlock = false;

      String(markdown || '')
        .replace(/\r\n?/g, '\n')
        .split('\n')
        .forEach((line) => {
          if (line.trimStart().startsWith('```')) {
            inCodeBlock = !inCodeBlock;
            return;
          }

          if (inCodeBlock) return;

          let hashes = 0;
          while (hashes < line.length && line[hashes] === '#') hashes += 1;

          if (hashes >= 1 && hashes <= CONST_WIZ_MAX_HEADING_LEVEL && line[hashes] === ' ') result.push({ level: hashes, text: line.slice(hashes).trim() });
        });

      return result;
    },
    /** พอร์ตการนับของ HeadingNodeBuilder — ราก 1 ตัวต่อไฟล์ + หัวข้อที่ไม่ถูกข้าม · เส้น = โหนดที่ยังหาแม่เจอ */
    estimateFile(markdown, skippedLevels) {
      const items = [{ level: 0, parent: -1 }];
      this.parseHeadings(markdown).forEach((h) => items.push({ level: h.level, parent: 0 }));

      for (let i = 1; i < items.length; i += 1) {
        for (let j = i - 1; j >= 1; j -= 1) {
          if (items[j].level < items[i].level) {
            items[i].parent = j;
            break;
          }
        }
      }

      items.forEach((item) => {
        item.skip = !!skippedLevels[item.level];
      });

      let nodes = 0;
      let edges = 0;

      items.forEach((item) => {
        if (item.skip) return;

        nodes += 1;

        let parent = item.parent;
        while (parent >= 0 && items[parent].skip) parent = items[parent].parent;

        if (parent >= 0) edges += 1;
      });

      return { nodes: nodes, edges: edges };
    },
    // ── โหมดตาราง CSV/XLSX (7-A) ────────────────────────────────────────────
    isStructuredFile(file) {
      return CONST_WIZ_TABULAR_EXT.includes(file.ext) && file.tableMode === 'structured';
    },
    sheetsOf(file) {
      return (this.state.structuredSheets || []).filter((s) => s.fileKey === file.key);
    },
    sheetsBusy(file) {
      return (this.state.sheetsLoading || []).indexOf(file.key) >= 0;
    },
    tableModeHint(file) {
      return file.tableMode === 'structured' ? this.$t('graphRagWizTableStructuredHint') : this.$t('graphRagWizTableStandardHint');
    },
    /** ป้ายท้ายชื่อไฟล์ในคิว: "(N ชีต — โหมดที่เลือก)" · ยังไม่เลือกโหมดต้องบอกว่ายังไม่เลือก */
    tabularNote(file) {
      if (!CONST_WIZ_TABULAR_EXT.includes(file.ext)) return '';

      const sheets = this.sheetsOf(file);
      if (!sheets.length) return '';

      let mode = this.$t('graphRagWizTableChoose');
      if (file.tableMode === 'structured') mode = this.$t('graphRagWizTableStructured');
      if (file.tableMode === 'standard') mode = this.$t('graphRagWizTableStandard');

      return this.$t('graphRagWizTableQueueNote', { count: sheets.length, mode: mode });
    },
    /**
     * เปลี่ยนโหมดของไฟล์เดียว — state เป็นของ GraphEditor (D10) จึงส่งอาร์เรย์ใหม่กลับไปทาง patch
     * คัดลอกเฉพาะแถวที่เปลี่ยน เพื่อไม่ให้แถวที่คิวแปลงถืออยู่เปลี่ยน identity กลางคัน
     */
    setTableMode(file, mode) {
      if (!this.canEdit || file.status !== 'done') return;

      // เลือกโหมดแล้วแผงต้องไม่หุบหนีไปเอง — ผู้ใช้ยังต้องตั้งค่ารายชีตต่อในแผงเดียวกัน
      this.tabularPanels = this.openTabularPanels.slice();
      this.patch({ files: (this.state.files || []).map((f) => (f.key === file.key ? { ...f, tableMode: mode } : f)) });
    },
    onTabularPanels(value) {
      this.tabularPanels = Array.isArray(value) ? value : [];
    },
    setAllTableMode(mode) {
      if (!this.canEdit) return;

      const keys = this.tabularDoneFiles.map((f) => f.key);
      this.patch({ files: (this.state.files || []).map((f) => (keys.indexOf(f.key) >= 0 ? { ...f, tableMode: mode } : f)) });
    },
    setSheetInclude(sheet, include) {
      if (!this.canEdit) return;

      this.patch({ structuredSheets: (this.state.structuredSheets || []).map((s) => (s.key === sheet.key ? { ...s, include: include } : s)) });
    },
    loadAllSheets() {
      this.tabularDoneFiles.forEach((file) => this.loadSheets(file));
    },
    /**
     * รายชื่อชีตของไฟล์ตาราง — ใช้ endpoint เดิมของการนำเข้าตาราง (GraphRagStructuredPreview)
     * เรียกครั้งเดียวต่อไฟล์ · ไฟล์ csv ที่ backend ไม่คืน sheetNames ให้ใช้ชื่อชีตเดียวที่คืนมา
     */
    loadSheets(file) {
      if (!file.session || this.sheetsOf(file).length || this.sheetsBusy(file)) return;

      this.patch({ sheetsLoading: (this.state.sheetsLoading || []).concat([file.key]) });

      const done = (rows) => {
        this.patch({
          structuredSheets: (this.state.structuredSheets || []).filter((s) => s.fileKey !== file.key).concat(rows),
          sheetsLoading: (this.state.sheetsLoading || []).filter((key) => key !== file.key),
        });
      };

      this.$call.GraphRagStructuredPreview(
        { graphKey: this.graphKey, sessionId: file.session.sessionId, sheetName: null },
        (res) => {
          if (!res || res.status !== 0 || !res.data) return done([]);

          // CSV ไม่มีชื่อชีต — ต้องได้แถวเดียวที่หมายถึง "ทั้งไฟล์" ไม่ใช่ไม่มีแถวเลย
          const found = res.data.sheetNames && res.data.sheetNames.length ? res.data.sheetNames : [res.data.sheetName || ''];

          return done(
            found.map((name) => ({
              key: this.slug(file.key + '--' + (name || 'default')),
              fileKey: file.key,
              fileName: file.name,
              sessionId: file.session.sessionId,
              sheetName: name,
              label: name || file.name,
              include: true,
              mapping: null,
              matchStrategy: 'by-node-key',
              onMissing: 'keep',
            }))
          );
        },
        () => done([])
      );
    },
    prettySize(bytes) {
      const mb = (bytes || 0) / (1024 * 1024);

      return mb >= 1 ? mb.toFixed(1) + ' MB' : Math.max(1, Math.round((bytes || 0) / 1024)) + ' KB';
    },
    statusText(f) {
      if (f.status === 'pending') return this.$t('graphRagWizStatusPending');
      if (f.status === 'converting') return this.$t('graphRagWizStatusConverting');
      if (f.status === 'error') return this.$t('graphRagWizStatusError');
      // FIX-4: นามสกุลที่ระบบไม่รับตั้งแต่ต้น ไม่เคยถูกส่งขึ้น backend ⇒ ไม่ใช่ "แปลงไม่สำเร็จ"
      if (f.status === 'unsupported') return this.$t('graphRagWizStatusUnsupported');
      if (f.session && f.session.isDuplicateFile) return this.$t('graphRagWizStatusDuplicate');

      return this.$t('graphRagWizStatusDone');
    },
    /**
     * FIX-4: `.json` เคยขึ้นว่า "ยังไม่รองรับ .doc และ .ppt แบบเก่า…" ซึ่งไม่ตรงเคสเลย
     * ข้อความของไฟล์รุ่นเก่ายังใช้กับ .doc/.ppt/.xls เหมือนเดิม (คีย์เดิมไม่ถูกลบ)
     */
    unsupportedNote(f) {
      return CONST_WIZ_LEGACY_EXT.indexOf(f.ext) >= 0 ? this.$t('graphRagUnsupportedNote') : this.$t('graphRagWizUnsupportedExt');
    },
    statusIcon(f) {
      if (f.status === 'error' || f.status === 'unsupported') return 'mdi-alert-circle';
      if (f.status === 'done') return f.session && f.session.isDuplicateFile ? 'mdi-information-outline' : 'mdi-check-circle';

      return 'mdi-clock-outline';
    },
    statusColour(f) {
      if (f.status === 'error' || f.status === 'unsupported') return 'error';
      if (f.status === 'done') return f.session && f.session.isDuplicateFile ? 'warning' : 'success';

      return '#646464';
    },
    /**
     * ปุ่ม "ลองใหม่" ต้องโผล่เฉพาะเคสที่ลองใหม่แล้วมีโอกาสผ่าน
     * ไฟล์ใหญ่เกินเพดาน / นามสกุลไม่รองรับ = ผิดกติกาถาวร ลองกี่รอบก็ได้ 400 เหมือนเดิม
     */
    canRetry(f) {
      if (f.status !== 'error') return false;
      if (!CONST_WIZ_ACCEPT.split(',').includes(f.ext)) return false;

      return (f.size || 0) <= CONST_WIZ_MAX_BYTES;
    },
    isSkipped(step) {
      return step === 2 && !this.editableMarkdownFiles.length;
    },
    canGoto(step) {
      if (this.isSkipped(step)) return false;

      return step <= this.state.maxStep;
    },
    /** คลิกช่องโมเดลแล้วเลือกข้อความทั้งช่อง — พิมพ์ทับเป็นคำค้นได้ทันที (Vuetify เติมชื่อที่เลือกไว้ตอนโฟกัส ต้องรอ tick) */
    selectModelText(focused) {
      const input = this.$refs.modelSelect?.$el?.querySelector('input');
      if (!focused || !input) return;

      setTimeout(() => input.select(), 0);
    },
    /**
     * ข้อความในช่องยังเป็นชื่อตัวที่เลือกอยู่ทั้งก้อน (เพิ่งคลิก · เพิ่งกด Enter เลือก) ⇒ ตัวอักษรที่พิมพ์ = เริ่มคำค้นใหม่ แทนที่ทั้งช่อง
     * select() ใน selectModelText อย่างเดียวไม่พอ: ตอนเปิดรายการหน้าเว็บค้าง ~160ms (วาดบอทหลายสิบตัว) ปุ่มที่พิมพ์ช่วงนั้นมาก่อน timer
     * และหลังกด Enter เลือก ช่องยังโฟกัสอยู่ คลิกซ้ำจึงไม่มี focus ใหม่ ⇒ ต่อท้ายชื่อเดิมเป็น "540 test-kanunGPT-4o" แล้วค้นไม่เจอ
     */
    onModelKeydown(event, title) {
      const input = event.target;
      if (!input || input.tagName !== 'INPUT' || !title) return;
      if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

      if (input.value === title) input.select();
    },
    goto(step) {
      const patch = { step: step, maxStep: Math.max(this.state.maxStep, step) };

      // ถอยออกจากขั้น 5 = จะแก้ค่าแล้วสร้างใหม่ ⇒ ลืมงานที่จบแล้วของรอบก่อน ปุ่มจะได้กลับมาเป็น "สร้างโหนด"
      // งานที่ยังวิ่งอยู่ไม่แตะ — ตัว poll ยังต้องใช้ jobId ต่อ
      if (this.state.step === 5 && step < 5 && this.state.jobId && !this.state.creating) {
        Object.assign(patch, { jobId: null, job: null, jobModelId: null, reviewSummary: null, progStep: 0, applyResult: null, applied: false, appliedFromReview: false, showProposals: false, createError: '', duplicateJob: false });
      }

      this.patch(patch);
    },
    onStepperNav(step) {
      if (typeof step === 'number' && step !== this.state.step && this.canGoto(step)) this.goto(step);
    },
    /** ขั้น 1 → 2 ถ้ามีไฟล์ markdown ที่แก้ได้ ไม่มีให้ข้ามไป 3 (ลอกจาก nexa) */
    goNext() {
      if (this.state.step === 1) return this.goto(this.editableMarkdownFiles.length ? 2 : 3);

      return this.goto(this.state.step + 1);
    },
    /** ขั้น 3 → 2 ถ้ามีไฟล์ที่แก้ได้ ไม่มีให้กลับไป 1 */
    goBack() {
      if (this.state.step === 3) return this.goto(this.editableMarkdownFiles.length ? 2 : 1);

      return this.goto(this.state.step - 1);
    },
    openPicker() {
      if (!this.canEdit) return;

      this.$refs.fileInput.click();
    },
    onPick(event) {
      this.$emit('add-files', [...event.target.files]);
      event.target.value = '';
    },
    onDrop(event) {
      this.dragOver = false;
      if (!this.canEdit) return;

      this.$emit('add-files', [...(event.dataTransfer ? event.dataTransfer.files : [])]);
    },
    /** การสลับโหมดล้างคิวทิ้ง — เจ้าของ state คือ GraphEditor (D10) ที่นี่แค่ขอ */
    switchMode(mode) {
      if (this.modeLocked || !this.canEdit || this.state.mode === mode) return;

      this.$emit('switch-mode', mode);
    },
    openFolderPicker() {
      if (!this.canEdit) return;

      this.$refs.folderInput.click();
    },
    onPickFolder(event) {
      this.$emit('add-files', [...event.target.files]);
      event.target.value = '';
    },
    /**
     * ลากวางทั้งโฟลเดอร์ — พอร์ตจาก traverseFolderDrop (nexa public/index.html:19950)
     *
     * ⚠️ ต้องเรียก webkitGetAsEntry() ให้ครบ "ก่อน" await ตัวแรก เพราะ DataTransferItemList
     *    ถูกล้างทันทีที่ handler คืนค่า — เรียกหลัง await จะได้ null ทุกตัวแบบเงียบ ๆ
     */
    async onFolderDrop(event) {
      this.dragOver = false;
      if (!this.canEdit) return;

      const transfer = event.dataTransfer;
      const entries = [...(transfer && transfer.items ? transfer.items : [])].map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null)).filter(Boolean);
      const plainFiles = [...(transfer ? transfer.files : [])];

      if (!entries.length) return this.$emit('add-files', plainFiles);

      const collected = [];

      for (const entry of entries) {
        // ลำดับสำคัญกว่าความเร็ว — ผังโฟลเดอร์ต้องเรียงตามที่ผู้ใช้ลากมา จึงรอทีละ entry
        collected.push(...(await this.collectEntry(entry, '')));
      }

      return this.$emit('add-files', collected.length ? collected : plainFiles);
    },
    /** ไล่ entry แบบ recursive แล้วติด path เต็มไว้กับ File เพื่อให้คีย์ยังเป็น {folderPath}::{name} (TC-43) */
    async collectEntry(entry, parentPath) {
      const path = parentPath ? parentPath + '/' + entry.name : entry.name;

      if (entry.isFile) {
        const file = await new Promise((resolve) => {
          entry.file(
            (found) => resolve(found),
            () => resolve(null)
          );
        });

        if (!file) return [];
        // File ที่ได้จาก entry ไม่มี webkitRelativePath — ฝากไว้ให้ GraphEditor.wizFolderPathOf อ่านแทน
        file.wizRelativePath = path;

        return [file];
      }

      if (!entry.isDirectory) return [];

      const children = await this.readAllEntries(entry.createReader());
      const result = [];

      for (const child of children) {
        result.push(...(await this.collectEntry(child, path)));
      }

      return result;
    },
    /** readEntries คืนทีละชุด (Chrome ได้ครั้งละ 100) ⇒ ต้องวนจนได้ชุดว่างถึงจะครบทั้งโฟลเดอร์ */
    readAllEntries(reader) {
      return new Promise((resolve) => {
        const all = [];
        const readBatch = () => {
          reader.readEntries(
            (batch) => {
              if (!batch.length) return resolve(all);

              all.push(...batch);

              return readBatch();
            },
            () => resolve(all)
          );
        };

        readBatch();
      });
    },
  },
};
</script>

<style scoped>
.wiz-body {
  padding-top: 0;
}
.wiz-stepper {
  box-shadow: none;
}
.wiz-window {
  overflow-x: auto;
}
.wiz-step-heading {
  font-size: 13px;
  font-weight: 600;
  outline: none;
}
.wiz-section {
  font-size: 15px;
}
.wiz-sub {
  font-size: 12px;
  color: #646464;
}
.wiz-file-name {
  font-size: 13px;
}
.wiz-drop {
  border: 2px dashed rgba(195, 46, 14, 0.45);
  border-radius: 8px;
  text-align: center;
  padding: 24px 12px;
  cursor: pointer;
}
.wiz-drop:hover,
.wiz-drop--over {
  border: 2px dashed #c32e0e;
  background: rgba(195, 46, 14, 0.06);
}
.wiz-drop:focus-visible {
  outline: 2px solid #c32e0e;
  outline-offset: 2px;
}
.wiz-drop--off {
  cursor: not-allowed;
  opacity: 0.6;
}
.wiz-drop-title {
  font-size: 13px;
}
.wiz-dots {
  padding: 8px 0;
}
.wiz-dots-row {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.wiz-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #d9d9d9;
}
.wiz-dot--on {
  background: #c32e0e;
}
.wiz-tree {
  max-height: 260px;
  overflow: auto;
}
.wiz-model {
  max-width: 440px;
}
.wiz-apply-warnings {
  font-size: 12px;
  padding-left: 18px;
}
/* ผังโฟลเดอร์กว้างได้ไม่จำกัด — ต้องเลื่อนในกล่องตัวเอง ไม่ใช่ดัน body ให้เลื่อนแนวนอน (กติกา Frontend ข้อ 9) */
.wiz-folder-tree {
  max-height: 200px;
  overflow: auto;
  border: 1px solid rgba(100, 100, 100, 0.25);
  border-radius: 6px;
  padding: 8px 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.wiz-folder-tree-head {
  padding-bottom: 4px;
}
.wiz-folder-tree-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  white-space: nowrap;
}
.wiz-folder-tree-icon {
  flex: 0 0 auto;
}
.wiz-folder-tree-name {
  overflow: hidden;
  text-overflow: ellipsis;
}
.wiz-prog {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.wiz-prog-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #646464;
}
.wiz-prog-item--on {
  color: #c32e0e;
}
.wiz-actions {
  position: sticky;
  bottom: 0;
  background: rgb(var(--v-theme-surface));
  padding-bottom: env(safe-area-inset-bottom);
}
.wiz-back-hole {
  display: inline-block;
  width: 96px;
}
/* ตัวรับ hover ของ tooltip ต้องกว้างเท่าปุ่มพอดี ไม่งั้นเมาส์ชี้ปุ่มแล้วไม่โดนตัวรับ */
.wiz-tip-wrap {
  display: inline-flex;
}
</style>
